import { memo, useState } from 'react';
import { CheckIcon, ExclamationTriangleIcon, PlayCircleIcon } from '@heroicons/react/24/outline';
import type { GalleryAsset } from '@xphoto/shared';
import { useI18n } from '../i18n';
import { thumbnailUrl } from './api';
import { interpolate } from './format';

/**
 * Tuile de vignette avec overlay au survol/focus (XPHOTO-19).
 *
 * - Boîte à ratio réservé (largeur/hauteur imposées) → **pas de saut de layout**
 *   pendant le chargement paresseux (`loading="lazy"` + skeleton).
 * - Overlay (case de sélection) révélé au survol ET au focus clavier ; toujours
 *   présent pour la case cochée (feedback persistant) et en MODE SÉLECTION.
 * - **Pas de nom/date** ici (réservés au futur panneau info, T3).
 * - `alt` = nom de fichier (accessibilité).
 *
 * MODE SÉLECTION EXPLICITE (v1.41.0) : le mode est un ÉTAT porté par `GalleryView`
 * (bouton « Sélectionner » de la barre d'outils) OU dérivé d'une sélection non vide —
 * `selectionMode = mode explicite || selected.size > 0`. La tuile n'en connaît que le
 * booléen résultant.
 *
 * Il REMPLACE le mode IMPLICITE de la v1.35.0 (motif Google Photos), où la case était
 * rendue visible en permanence sur les pointeurs grossiers via la variante
 * `[@media(hover:none)]:opacity-100`. À l'usage sur téléphone, une case blanche sur
 * CHAQUE tuile rendait la grille illisible : hors mode, la case est donc de nouveau
 * invisible tant qu'il n'y a ni survol, ni focus, ni coche. Le tactile n'a plus besoin
 * du survol — il a le bouton « Sélectionner » ; le survol reste la voie du pointeur fin,
 * et cocher au survol fait entrer dans le mode tout seul (`selected.size > 0`).
 *
 * En mode, un tap N'IMPORTE OÙ sur une tuile BASCULE sa sélection au lieu d'ouvrir la
 * visionneuse. La zone plein cadre change donc d'ACTION — son nom accessible et son rôle
 * DOIVENT suivre : un lecteur d'écran ne doit jamais annoncer « Ouvrir » pour un contrôle
 * qui coche. D'où le choix de structure : **un seul contrôle à la fois** sur la tuile.
 *  - hors mode sélection : la zone plein cadre est un `button` « Ouvrir … », et la
 *    petite case (overlay) est LA case à cocher (`role="checkbox"`) ;
 *  - en mode sélection : la zone plein cadre DEVIENT la case à cocher, et la petite
 *    case retombe en simple INDICATEUR visuel (`aria-hidden`, non focusable).
 * Jamais deux contrôles superposés au même nom : l'arbre d'accessibilité expose
 * exactement une case à cocher par tuile, quel que soit le mode.
 */
interface ThumbnailProps {
  asset: GalleryAsset;
  /** Index absolu dans la grille (pour ouvrir la visionneuse à la bonne position). */
  index: number;
  width: number;
  height: number;
  size: 256 | 1024;
  selected: boolean;
  /**
   * Mode sélection actif (bouton « Sélectionner » OU au moins une tuile cochée) : la
   * tuile ENTIÈRE coche au lieu d'ouvrir. Booléen (primitif) pour rester compatible
   * `memo` — la valeur est résolue par `GalleryView` et descend telle quelle.
   */
  selectionMode: boolean;
  /**
   * Bascule la sélection de cette tuile. `index` et `shiftKey` sont remontés pour que
   * la GRILLE (seule à savoir résoudre un index → asset) traite le maj-clic de plage.
   */
  onToggleSelect: (id: string, index: number, shiftKey: boolean) => void;
  /** Ouvre la visionneuse plein écran sur ce média (T3, XPHOTO-21). */
  onOpen: (index: number) => void;
}

/** Case de sélection : 28 px (confort tactile ; le minimum AA 2.5.8 est 24 px). */
const CHECK_BOX_BASE =
  'flex h-7 w-7 items-center justify-center rounded-md border transition-colors motion-reduce:transition-none';

function ThumbnailImpl({
  asset,
  index,
  width,
  height,
  size,
  selected,
  selectionMode,
  onToggleSelect,
  onOpen,
}: ThumbnailProps): React.JSX.Element {
  const { t } = useI18n();
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  const alt = interpolate(t('gallery.thumbnailAlt'), { name: asset.filename });
  const selectLabel = interpolate(t('select.toggle'), { name: asset.filename });
  const openLabel = interpolate(t('viewer.open'), { name: asset.filename });

  const toggle = (event: React.MouseEvent): void => {
    onToggleSelect(asset.id, index, event.shiftKey);
  };

  return (
    <div
      className="group relative overflow-hidden rounded-md bg-surface-2"
      style={{ width, height }}
    >
      {/* Skeleton : reste sous l'image jusqu'à son chargement (anti-CLS). */}
      {!loaded && !failed && (
        <div className="absolute inset-0 animate-pulse bg-surface-2 motion-reduce:animate-none" />
      )}

      {failed ? (
        <div className="flex h-full w-full items-center justify-center text-muted">
          <ExclamationTriangleIcon className="h-6 w-6" aria-hidden="true" />
        </div>
      ) : (
        <img
          src={thumbnailUrl(asset.id, size, asset.rev)}
          alt={alt}
          width={width}
          height={height}
          loading="lazy"
          decoding="async"
          // Priorité BASSE : sur une grande grille, des centaines de vignettes se
          // chargent en rafale ; en HTTP/2 (Caddy) elles cèdent la priorité de flux
          // aux requêtes de données (liste/compteurs/admin) marquées « high ».
          fetchPriority="low"
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
          className={`h-full w-full object-cover transition-opacity duration-200 motion-reduce:transition-none ${
            loaded ? 'opacity-100' : 'opacity-0'
          }`}
        />
      )}

      {/* Indicateur vidéo (icône + libellé accessible). */}
      {asset.kind === 'video' && (
        <span
          className="pointer-events-none absolute bottom-1 start-1 text-fg drop-shadow"
          title={t('badge.video')}
        >
          <PlayCircleIcon className="h-6 w-6" aria-hidden="true" />
          <span className="sr-only">{t('badge.video')}</span>
        </span>
      )}

      {/* Absence disque : badge texte + icône (jamais la couleur seule). */}
      {asset.isMissing && (
        <span className="absolute bottom-1 end-1 inline-flex items-center gap-1 rounded-md bg-surface/85 px-1.5 py-0.5 text-xs font-medium text-danger">
          <ExclamationTriangleIcon className="h-4 w-4" aria-hidden="true" />
          <span>{t('badge.missing')}</span>
        </span>
      )}

      {/* Zone plein cadre — UNIQUE contrôle principal de la tuile, dont le rôle suit le
          mode (cf. en-tête). Hors mode sélection : « Ouvrir … » (la petite case, qui
          vient après dans le DOM et porte `pointer-events-auto`, reste prioritaire).
          En mode sélection : la tuile entière EST la case à cocher. */}
      {selectionMode ? (
        <button
          type="button"
          role="checkbox"
          aria-checked={selected}
          aria-label={selectLabel}
          title={selectLabel}
          onClick={toggle}
          className="absolute inset-0 z-0 cursor-pointer focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand"
        />
      ) : (
        <button
          type="button"
          aria-label={openLabel}
          title={openLabel}
          onClick={() => onOpen(index)}
          className="absolute inset-0 z-0 cursor-zoom-in focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand"
        />
      )}

      {/* Overlay : TOUJOURS visible en mode sélection ou si la tuile est cochée ; sinon
          masqué et révélé au survol/focus clavier seulement. Aucune variante
          `[@media(hover:none)]` (retirée en v1.41.0) : sur un écran tactile, hors mode,
          la grille ne porte AUCUNE case — c'est le bouton « Sélectionner » qui les fait
          apparaître, toutes à la fois. */}
      <div
        className={`pointer-events-none absolute inset-0 z-10 flex flex-col justify-between p-1 transition-opacity motion-reduce:transition-none ${
          selected || selectionMode
            ? 'opacity-100'
            : 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100'
        }`}
      >
        <div className="flex items-start justify-between gap-1">
          {selectionMode ? (
            // Mode sélection : la tuile entière porte déjà `role="checkbox"` +
            // `aria-checked` → cette case n'est plus qu'un REPÈRE VISUEL. Ni bouton ni
            // focusable, et retirée de l'arbre d'accessibilité (sinon la tuile
            // annoncerait deux fois la même case, au même nom).
            <span
              aria-hidden="true"
              className={`${CHECK_BOX_BASE} ${
                selected
                  ? 'border-brand bg-brand text-brand-fg'
                  : 'border-border bg-surface/85 text-transparent'
              }`}
            >
              <CheckIcon className="h-4 w-4" />
            </span>
          ) : (
            // Hors mode : la case est le SEUL moyen de cocher (un tap sur la tuile
            // ouvre) — focusable au clavier, révélée au survol. La cocher fait entrer
            // dans le mode (`selected.size > 0`), sans passer par le bouton.
            <button
              type="button"
              role="checkbox"
              aria-checked={selected}
              aria-label={selectLabel}
              title={selectLabel}
              onClick={toggle}
              className={`pointer-events-auto ${CHECK_BOX_BASE} focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand ${
                selected
                  ? 'border-brand bg-brand text-brand-fg'
                  : 'border-border bg-surface/85 text-transparent hover:text-muted'
              }`}
            >
              <CheckIcon className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Mémoïsée : sur une grille virtualisée, un changement d'état SANS RAPPORT dans
 * `GalleryView` (ouverture d'une modale, drapeau d'occupation…) re-rendait TOUTES les
 * tuiles montées. Les props sont primitives + callbacks stables (`useCallback`),
 * `asset` vient du layout mémoïsé et `selected` ne varie que pour la tuile concernée
 * → `memo` supprime ce churn de rendu sans changer le comportement.
 *
 * `selectionMode` respecte ce contrat : c'est un BOOLÉEN (`mode explicite ||
 * selected.size > 0`) résolu une fois par `GalleryView` et non le `Set`, dont l'identité
 * change à chaque bascule ; il ne varie donc qu'aux transitions qui changent réellement
 * l'affichage — entrée/sortie du mode, première tuile cochée, sélection vidée. Toute prop
 * ajoutée ici doit rester primitive ou stable : un objet ou une lambda inline
 * réintroduirait le churn sur toute la grille.
 */
export const Thumbnail = memo(ThumbnailImpl);
